<template>
  <!-- GEO_EXEC_R18 · the one thing to do next. Nothing to do, or the switch off → nothing drawn. -->
  <!-- No `emphasis`: the heading already says this is the one thing. `Start here` marks the
       first row on the plan, where several rows compete for it. -->
  <section v-if="row" class="nxa" :class="`nxa-${variant}`" :aria-label="heading">
    <div class="nxa-h">
      <span class="nxa-lab">{{ heading }}</span>
      <span v-if="more > 0" class="nxa-n">{{ more }} more waiting</span>
    </div>
    <RecommendationCard :row="row" :compact="variant === 'row'" @changed="load" @gone="row = null" />
    <router-link v-if="variant === 'row'" class="nxa-all" to="/plan">See the plan</router-link>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { getNextAction } from "../api/geoExec";
import { featureHidden, noteFeature } from "../stores/geoExecFeatures";
import RecommendationCard from "./RecommendationCard.vue";

const props = defineProps({
  brandId: { type: [Number, String], default: null },
  variant: { type: String, default: "row" }, // "row" = inside a brand card · "card" = Maya's first screen
});

const row = ref(null);
const pending = ref(0);
let controller;

const heading = computed(() => (props.variant === "row" ? "What's still missing" : "Next from Maya"));
const more = computed(() => Math.max(0, Number(pending.value || 0) - 1));

async function load() {
  if (!props.brandId || featureHidden("recommendations")) {
    row.value = null;
    return;
  }
  controller?.abort();
  controller = new AbortController();
  try {
    const payload = await getNextAction(props.brandId, { signal: controller.signal });
    noteFeature("recommendations", null);
    row.value = payload?.next_action || null;
    pending.value = Number(payload?.pending || 0);
  } catch (caught) {
    if (caught.name === "AbortError") return;
    // Never an error line here: this block is an extra on somebody else's card, and a brand card
    // that turns into a red banner because one optional read failed is a worse card.
    noteFeature("recommendations", caught);
    row.value = null;
  }
}

onMounted(load);
watch(() => props.brandId, load);
onBeforeUnmount(() => controller?.abort());
</script>

<style scoped>
.nxa {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--separator);
}

.nxa-card {
  margin: 0 0 16px;
  padding-top: 0;
  border-top: 0;
}

.nxa-h {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.nxa-lab {
  font-size: var(--fs-caption);
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--ink);
}

.nxa-n {
  font-size: var(--fs-caption2);
  color: var(--mute);
  font-variant-numeric: tabular-nums;
}

.nxa-all {
  display: inline-block;
  margin-top: 8px;
  min-height: 32px;
  color: var(--indigo);
  font-size: var(--fs-caption);
  font-weight: 650;
}

.nxa-all:hover {
  text-decoration: underline;
}
</style>
